import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Menu, X, LogOut, User as UserIcon } from "lucide-react";

const navItems = [
  { to: "/", label: "Home", end: true },
  { to: "/films", label: "Films" },
  { to: "/productions", label: "Productions" },
  { to: "/festival", label: "Festivals & Events" },
  { to: "/network", label: "Network" },
  { to: "/membership", label: "Membership" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      className="sticky top-0 z-50 bg-[#FFF8E7]/90 backdrop-blur-xl border-b border-[#0A0A0A]"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
          <img
            src="/divergent-cinema-logo.png"
            alt="Divergent Cinema Film Festival logo"
            className="h-12 w-auto object-contain"
          />
          <span className="font-heading font-bold text-[#0A0A0A] text-lg tracking-tight">
            Divergent Cinema <span className="text-[#1A5DFF] text-sm">CIC</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2E63] px-1 ${
                  isActive ? "text-[#FF2E63]" : "text-[#0A0A0A] hover:text-[#FF2E63]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `text-sm font-bold uppercase tracking-[0.16em] px-2 py-1 border-2 border-[#0A0A0A] transition-colors ${
                  isActive ? "bg-[#0A0A0A] text-[#FFF8E7]" : "bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05]"
                }`
              }
            >
              Admin
            </NavLink>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-[#2C2C2C]" data-testid="nav-user">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <UserIcon className="w-5 h-5" />
                )}
                <span className="max-w-[140px] truncate">{user.name}</span>
              </div>
              <button
                onClick={signOut}
                data-testid="nav-signout"
                className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] rounded-none px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0A0A0A] inline-flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          ) : (
            <Link
              to={`/signin?next=${encodeURIComponent(location.pathname)}`}
              data-testid="nav-signin"
              className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] rounded-none px-5 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF2E63]"
            >
              Member sign in
            </Link>
          )}
        </div>

        <button
          className="md:hidden text-[#0A0A0A] p-2 focus:outline-none focus:ring-2 focus:ring-[#FF2E63]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#0A0A0A] bg-[#FFF8E7]" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-3">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-2 text-base font-medium ${isActive ? "text-[#FF2E63]" : "text-[#0A0A0A]"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-2 border-t border-[#0A0A0A]">
              {user ? (
                <button
                  onClick={() => { setOpen(false); signOut(); }}
                  className="w-full bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] rounded-none px-4 py-2 text-sm font-medium"
                  data-testid="mobile-nav-signout"
                >
                  Sign out ({user.name})
                </button>
              ) : (
                <Link
                  to="/signin"
                  onClick={() => setOpen(false)}
                  className="block text-center bg-[#FF2E63] text-[#FFF8E7] rounded-none px-4 py-2 text-sm font-medium"
                  data-testid="mobile-nav-signin"
                >
                  Member sign in
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
